import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Typography,
  TextField,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Alert,
  Chip,
  FormGroup,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import HighlightIcon from '@mui/icons-material/Highlight';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import FormatIndentIncreaseIcon from '@mui/icons-material/FormatIndentIncrease';
import FolderIcon from '@mui/icons-material/Folder';
import AddIcon from '@mui/icons-material/Add';
import ReusableModal from '../../../components/Modal/ReusableModal';
import useThemeStyles from '../../../hooks/useThemeStyles';
import projectService from '../../../utils/apiRequests/projectService';
import importService from '../../../utils/apiRequests/importService';
import { PROJECT_TYPE_OPTIONS } from '../../../utils/projectTypeConfig';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

const PROJECT_TYPES = PROJECT_TYPE_OPTIONS;
const IMPORT_PROJECT_TYPES = PROJECT_TYPE_OPTIONS;

const STEPS_SCRATCH = ['Get Started', 'Project Details'];
const STEPS_IMPORT = [
  'Get Started',
  'Upload File',
  'Project',
  'Formatting',
  'Chapter Detection',
  'Review',
];

const CreateProjectModal = ({ open, onClose, onProjectCreated, onImportComplete, projects }) => {
  const { colors } = useThemeStyles();

  // Step 0 – method selection
  const [selectedMethod, setSelectedMethod] = useState(null); // null | 'scratch' | 'import'
  const [activeStep, setActiveStep] = useState(0);

  // Scratch path
  const [scratchTitle, setScratchTitle] = useState('');
  const [scratchType, setScratchType] = useState('novel');
  const [scratchDescription, setScratchDescription] = useState('');

  // Import path – file
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  // Import path – project selection
  const [projectMode, setProjectMode] = useState('new'); // 'new' | 'existing'
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [importProjectTitle, setImportProjectTitle] = useState('');
  const [importProjectType, setImportProjectType] = useState('novel');
  const [createdProjectId, setCreatedProjectId] = useState(null);

  // Import path – formatting & chapter detection
  const [formattingOptions, setFormattingOptions] = useState({
    bold: true,
    italic: true,
    underline: true,
    strikethrough: true,
    highlight: true,
    color: true,
    // Off by default: real first-line indents from the document are preserved
    // automatically; this option force-adds a tab and breaks 1:1 round trips.
    tab: false,
  });
  const [chapterPattern, setChapterPattern] = useState('');
  const [usePageBreaks, setUsePageBreaks] = useState(true);
  const [skipTitlePage, setSkipTitlePage] = useState(false);
  // AI extraction from the manuscript (characters, timeline events)
  const [extractCharacters, setExtractCharacters] = useState(true);
  const [extractTimeline, setExtractTimeline] = useState(true);
  const [preview, setPreview] = useState(null);

  // Common
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const effectiveProjectId = createdProjectId || selectedProjectId;

  const steps = useMemo(() => {
    if (selectedMethod === 'scratch') return STEPS_SCRATCH;
    if (selectedMethod === 'import') return STEPS_IMPORT;
    return ['Get Started'];
  }, [selectedMethod]);

  const isLastStep = activeStep === steps.length - 1;
  const isScratchLastStep = selectedMethod === 'scratch' && isLastStep;
  const isImportLastStep = selectedMethod === 'import' && isLastStep;

  // ── Reset ──────────────────────────────────────────────────────────────────

  const resetState = () => {
    setSelectedMethod(null);
    setActiveStep(0);
    setScratchTitle('');
    setScratchType('novel');
    setScratchDescription('');
    setFile(null);
    setDragOver(false);
    setProjectMode('new');
    setSelectedProjectId('');
    setImportProjectTitle('');
    setImportProjectType('novel');
    setCreatedProjectId(null);
    setFormattingOptions({
      bold: true,
      italic: true,
      underline: true,
      strikethrough: true,
      highlight: true,
      color: true,
      tab: false,
    });
    setChapterPattern('');
    setUsePageBreaks(true);
    setSkipTitlePage(false);
    setExtractCharacters(true);
    setExtractTimeline(true);
    setPreview(null);
    setLoading(false);
    setError('');
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  // ── File handling ──────────────────────────────────────────────────────────

  const validateFile = (selectedFile) => {
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
    ];
    const allowedExtensions = ['.docx', '.doc'];
    const fileName = selectedFile.name.toLowerCase();
    const hasValidExtension = allowedExtensions.some((ext) => fileName.endsWith(ext));

    if (!allowedTypes.includes(selectedFile.type) && !hasValidExtension) {
      setError('Only Microsoft Word documents (.docx, .doc) are supported.');
      return false;
    }
    if (selectedFile.size > MAX_FILE_SIZE) {
      setError('File size must not exceed 10 MB.');
      return false;
    }
    return true;
  };

  const handleFileSelect = useCallback(
    (selectedFile) => {
      setError('');
      if (validateFile(selectedFile)) {
        setFile(selectedFile);
        if (projectMode === 'new') {
          const nameWithoutExt = selectedFile.name.replace(/\.[^/.]+$/, '');
          setImportProjectTitle(nameWithoutExt);
        }
      }
    },
    [projectMode] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleFileChange = (event) => {
    const selected = event.target.files[0];
    if (selected) handleFileSelect(selected);
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile) handleFileSelect(droppedFile);
    },
    [handleFileSelect]
  );

  const handleFormattingChange = (option) => {
    setFormattingOptions((prev) => ({ ...prev, [option]: !prev[option] }));
  };

  // ── Navigation ─────────────────────────────────────────────────────────────

  const canProceed = () => {
    const label = steps[activeStep];
    switch (label) {
      case 'Get Started':
        return selectedMethod === 'scratch' || selectedMethod === 'import';
      case 'Project Details':
        return scratchTitle.trim() !== '';
      case 'Upload File':
        return file !== null;
      case 'Project':
        return projectMode === 'new' || selectedProjectId !== '';
      case 'Review':
        return preview !== null;
      default:
        return true;
    }
  };

  const handleNext = async () => {
    setError('');
    const label = steps[activeStep];

    if (label === 'Chapter Detection') {
      setLoading(true);
      try {
        const response = await importService.previewDocument(file, {
          chapterPattern,
          usePageBreaks,
          skipTitlePage,
        });
        if (response.error) {
          setError(response.error?.response?.data?.error || 'Failed to preview document');
          setLoading(false);
          return;
        }
        setPreview(response.data);
        setActiveStep((prev) => prev + 1);
      } catch {
        setError('Failed to preview document. Please try again.');
      } finally {
        setLoading(false);
      }
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setError('');
  };

  // ── Submit handlers ────────────────────────────────────────────────────────

  const handleCreateScratch = async () => {
    if (!scratchTitle.trim()) {
      setError('Project title is required.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await projectService.createProject({
        title: scratchTitle.trim(),
        type: scratchType,
        description: scratchDescription.trim() || undefined,
      });
      if (response?.data?.project) {
        onProjectCreated(response.data.project);
        handleClose();
      } else {
        setError('Failed to create project. Please try again.');
      }
    } catch {
      setError('Failed to create project. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    setLoading(true);
    setError('');
    try {
      let targetProjectId = effectiveProjectId;

      if (projectMode === 'new' && !createdProjectId) {
        const projectTitle =
          importProjectTitle.trim() || file?.name?.replace(/\.[^/.]+$/, '') || 'Imported Document';
        const projectResponse = await projectService.createProject({
          title: projectTitle,
          type: importProjectType,
        });
        if (projectResponse.error) {
          setError(projectResponse.error?.response?.data?.error || 'Failed to create project');
          setLoading(false);
          return;
        }
        targetProjectId = projectResponse.data?.project?._id;
        if (!targetProjectId) {
          setError('Failed to create project. Please try again.');
          setLoading(false);
          return;
        }
        setCreatedProjectId(targetProjectId);
      }

      const response = await importService.importDocument(targetProjectId, file, {
        ...formattingOptions,
        chapterPattern,
        usePageBreaks,
        skipTitlePage,
        importCharacters: extractCharacters,
        importTimeline: extractTimeline,
      });

      if (response.error) {
        setError(response.error?.response?.data?.error || 'Failed to import document');
        setLoading(false);
        return;
      }

      if (onImportComplete) {
        onImportComplete(response.data, projectMode === 'new');
      }
      handleClose();
    } catch {
      setError('Failed to import document. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Step renderers ─────────────────────────────────────────────────────────

  const renderMethodCard = ({ icon, title, description, method, disabled, comingSoon }) => {
    const isSelected = selectedMethod === method;
    return (
      <Box
        key={method || title}
        onClick={disabled ? undefined : () => setSelectedMethod(method)}
        data-testid={`method-card-${method || 'template'}`}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          p: 2.5,
          border: `2px solid ${
            isSelected
              ? colors?.primary?.main || '#1976d2'
              : colors?.border?.subtle || 'rgba(148,163,184,0.3)'
          }`,
          borderRadius: 2,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          backgroundColor: isSelected ? `${colors?.primary?.main || '#1976d2'}12` : 'transparent',
          transition: 'all 0.15s ease',
          ...(!disabled && {
            '&:hover': {
              borderColor: colors?.primary?.main || '#1976d2',
              backgroundColor: `${colors?.primary?.main || '#1976d2'}08`,
            },
          }),
        }}
      >
        <Box
          sx={{
            color: isSelected
              ? colors?.primary?.main || '#1976d2'
              : colors?.text?.secondary || 'text.secondary',
            mt: 0.25,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: isSelected
                  ? colors?.primary?.main || '#1976d2'
                  : colors?.text?.primary || 'text.primary',
              }}
            >
              {title}
            </Typography>
            {comingSoon && (
              <Chip
                label="Coming Soon"
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  backgroundColor: colors?.background?.secondary || 'rgba(148,163,184,0.15)',
                  color: colors?.text?.secondary || 'text.secondary',
                }}
              />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            {description}
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderGetStartedStep = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 1 }}>
        How would you like to start your project?
      </Typography>

      {renderMethodCard({
        icon: <DriveFileRenameOutlineIcon />,
        title: 'Start from scratch',
        description: 'Begin with a blank project and build your story from the ground up.',
        method: 'scratch',
        disabled: false,
        comingSoon: false,
      })}

      {renderMethodCard({
        icon: <UploadFileIcon />,
        title: 'Import a document',
        description:
          'Import an existing Word document (.docx, .doc) into a new or existing project.',
        method: 'import',
        disabled: false,
        comingSoon: false,
      })}

      {renderMethodCard({
        icon: <AutoAwesomeIcon />,
        title: 'Start from a template',
        description: 'Use a pre-built template to jump-start your writing project.',
        method: 'template',
        disabled: true,
        comingSoon: true,
      })}
    </Box>
  );

  const renderProjectDetailsStep = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
        Fill in the details for your new project.
      </Typography>

      <TextField
        label="Project Title"
        value={scratchTitle}
        onChange={(e) => setScratchTitle(e.target.value)}
        fullWidth
        required
        disabled={loading}
        autoFocus
        sx={{
          '& .MuiInputLabel-root': { color: colors?.text?.secondary || 'text.secondary' },
          '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
          input: { color: colors?.text?.primary || 'text.primary' },
          '& .MuiOutlinedInput-root': {
            '& fieldset': { borderColor: colors?.border?.subtle || 'divider' },
            '&:hover fieldset': { borderColor: 'primary.main' },
            '&.Mui-focused fieldset': { borderColor: 'primary.main' },
          },
          '& input:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 100px var(--secondary-bg) inset',
            WebkitTextFillColor: colors?.text?.primary || 'inherit',
            transition: 'background-color 5000s ease-in-out 0s',
          },
        }}
      />

      <TextField
        select
        label="Project Type"
        value={scratchType}
        onChange={(e) => setScratchType(e.target.value)}
        fullWidth
        disabled={loading}
        sx={{
          '& .MuiInputLabel-root': { color: colors?.text?.secondary || 'text.secondary' },
          '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
          '& .MuiOutlinedInput-root': {
            '& fieldset': { borderColor: colors?.border?.subtle || 'divider' },
            '&:hover fieldset': { borderColor: 'primary.main' },
            '&.Mui-focused fieldset': { borderColor: 'primary.main' },
          },
          '& .MuiSelect-select': { color: colors?.text?.primary || 'text.primary' },
          '& .MuiSvgIcon-root': { color: colors?.text?.secondary || 'text.secondary' },
        }}
      >
        {PROJECT_TYPES.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            <Box>
              <Typography variant="body1">{option.label}</Typography>
              <Typography variant="caption" color="text.secondary">
                {option.description}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </TextField>

      <TextField
        label="Description (Optional)"
        value={scratchDescription}
        onChange={(e) => setScratchDescription(e.target.value)}
        fullWidth
        multiline
        rows={3}
        disabled={loading}
        sx={{
          '& .MuiInputLabel-root': { color: colors?.text?.secondary || 'text.secondary' },
          '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
          '& .MuiInputBase-input': { color: colors?.text?.primary || 'text.primary' },
          '& .MuiOutlinedInput-root': {
            '& fieldset': { borderColor: colors?.border?.subtle || 'divider' },
            '&:hover fieldset': { borderColor: 'primary.main' },
            '&.Mui-focused fieldset': { borderColor: 'primary.main' },
          },
        }}
      />
    </Box>
  );

  const renderFileUploadStep = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
        Upload a Microsoft Word document (.docx or .doc) to import into your project.
      </Typography>

      <Box
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        component="label"
        sx={{
          border: `2px dashed ${
            dragOver
              ? colors?.primary?.main || '#1976d2'
              : colors?.border?.subtle || 'rgba(148,163,184,0.4)'
          }`,
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          backgroundColor: dragOver ? `${colors?.primary?.main || '#1976d2'}10` : 'transparent',
          '&:hover': {
            borderColor: colors?.primary?.main || '#1976d2',
            backgroundColor: `${colors?.primary?.main || '#1976d2'}05`,
          },
        }}
      >
        <input
          type="file"
          hidden
          accept=".docx,.doc,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
          onChange={handleFileChange}
        />
        <CloudUploadIcon
          sx={{
            fontSize: 48,
            color: dragOver
              ? colors?.primary?.main || '#1976d2'
              : colors?.text?.secondary || 'gray',
            mb: 2,
          }}
        />
        <Typography variant="h6" sx={{ mb: 1 }}>
          {dragOver ? 'Drop file here' : 'Drag & drop your file here'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          or click to browse
        </Typography>
      </Box>

      {file && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 2,
            backgroundColor: colors?.paper?.elevated || 'rgba(148,163,184,0.08)',
            borderRadius: 1,
          }}
        >
          <DescriptionIcon sx={{ color: colors?.primary?.main || '#1976d2' }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1">{file.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {(file.size / 1024).toFixed(1)} KB
            </Typography>
          </Box>
          <Button size="small" color="error" onClick={() => setFile(null)}>
            Remove
          </Button>
        </Box>
      )}
    </Box>
  );

  const renderProjectSelectionStep = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
        Choose where to import your document.
      </Typography>

      <RadioGroup
        value={projectMode}
        onChange={(e) => {
          setProjectMode(e.target.value);
          if (e.target.value === 'new') setSelectedProjectId('');
        }}
      >
        <FormControlLabel
          value="new"
          control={<Radio sx={{ color: colors?.primary?.main || '#1976d2' }} />}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AddIcon sx={{ color: colors?.icon?.primary || '#666' }} />
              <Typography fontWeight={projectMode === 'new' ? 'bold' : 'normal'}>
                Create New Project
              </Typography>
            </Box>
          }
          sx={{
            border:
              projectMode === 'new'
                ? `2px solid ${colors?.primary?.main || '#1976d2'}`
                : '2px solid transparent',
            borderRadius: 1,
            p: 1,
            m: 0,
            backgroundColor:
              projectMode === 'new' ? `${colors?.primary?.main || '#1976d2'}10` : 'transparent',
          }}
        />

        <Collapse in={projectMode === 'new'}>
          <Box sx={{ pl: 4, pr: 2, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Project Title"
              value={importProjectTitle}
              onChange={(e) => setImportProjectTitle(e.target.value)}
              fullWidth
              placeholder="Leave empty to use file name"
              size="small"
              sx={{
                '& .MuiInputLabel-root': { color: colors?.text?.secondary || 'text.secondary' },
                '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
                input: { color: colors?.text?.primary || 'text.primary' },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: colors?.border?.subtle || 'divider' },
                  '&:hover fieldset': { borderColor: 'primary.main' },
                  '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                },
              }}
            />
            <TextField
              select
              label="Project Type"
              value={importProjectType}
              onChange={(e) => setImportProjectType(e.target.value)}
              fullWidth
              size="small"
              sx={{
                '& .MuiInputLabel-root': { color: colors?.text?.secondary || 'text.secondary' },
                '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: colors?.border?.subtle || 'divider' },
                  '&:hover fieldset': { borderColor: 'primary.main' },
                  '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                },
                '& .MuiSelect-select': { color: colors?.text?.primary || 'text.primary' },
              }}
            >
              {IMPORT_PROJECT_TYPES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Collapse>

        {projects && projects.length > 0 && (
          <>
            <FormControlLabel
              value="existing"
              control={<Radio sx={{ color: colors?.primary?.main || '#1976d2' }} />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FolderIcon sx={{ color: colors?.icon?.primary || '#666' }} />
                  <Typography fontWeight={projectMode === 'existing' ? 'bold' : 'normal'}>
                    Import to Existing Project
                  </Typography>
                </Box>
              }
              sx={{
                border:
                  projectMode === 'existing'
                    ? `2px solid ${colors?.primary?.main || '#1976d2'}`
                    : '2px solid transparent',
                borderRadius: 1,
                p: 1,
                m: 0,
                mt: 1,
                backgroundColor:
                  projectMode === 'existing'
                    ? `${colors?.primary?.main || '#1976d2'}10`
                    : 'transparent',
              }}
            />

            <Collapse in={projectMode === 'existing'}>
              <List sx={{ maxHeight: 180, overflowY: 'auto', pl: 2 }}>
                {projects.map((project) => (
                  <ListItem key={project._id} disablePadding>
                    <ListItemButton
                      selected={selectedProjectId === project._id}
                      onClick={() => setSelectedProjectId(project._id)}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        py: 0.5,
                        border:
                          selectedProjectId === project._id
                            ? `1px solid ${colors?.primary?.main || '#1976d2'}`
                            : '1px solid transparent',
                        '&.Mui-selected': {
                          backgroundColor: `${colors?.primary?.main || '#1976d2'}15`,
                        },
                        '&:hover': {
                          backgroundColor: `${colors?.primary?.main || '#1976d2'}05`,
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Radio
                          checked={selectedProjectId === project._id}
                          size="small"
                          sx={{ color: colors?.primary?.main || '#1976d2' }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={project.title || 'Untitled Project'}
                        secondary={
                          project.type
                            ? `${project.type} • ${project.thinChapters?.length || 0} chapters`
                            : `${project.thinChapters?.length || 0} chapters`
                        }
                        primaryTypographyProps={{
                          fontSize: '0.9rem',
                          fontWeight: selectedProjectId === project._id ? 'bold' : 'normal',
                        }}
                        secondaryTypographyProps={{ fontSize: '0.75rem' }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </>
        )}
      </RadioGroup>
    </Box>
  );

  const renderFormattingStep = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
        Choose which formatting to preserve from your document.
      </Typography>

      <FormGroup>
        {[
          { key: 'bold', icon: <FormatBoldIcon fontSize="small" />, label: 'Bold' },
          { key: 'italic', icon: <FormatItalicIcon fontSize="small" />, label: 'Italic' },
          { key: 'underline', icon: <FormatUnderlinedIcon fontSize="small" />, label: 'Underline' },
          {
            key: 'strikethrough',
            icon: <StrikethroughSIcon fontSize="small" />,
            label: 'Strikethrough',
          },
          { key: 'highlight', icon: <HighlightIcon fontSize="small" />, label: 'Highlighting' },
          { key: 'color', icon: <ColorLensIcon fontSize="small" />, label: 'Text Color' },
          {
            key: 'tab',
            icon: <FormatIndentIncreaseIcon fontSize="small" />,
            label: 'Force paragraph indents (adds a tab to every paragraph)',
          },
        ].map(({ key, icon, label }) => (
          <FormControlLabel
            key={key}
            control={
              <Checkbox
                checked={formattingOptions[key]}
                onChange={() => handleFormattingChange(key)}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {icon}
                <span>{label}</span>
              </Box>
            }
          />
        ))}
      </FormGroup>
    </Box>
  );

  const renderChapterDetectionStep = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
        Configure how chapters should be detected in your document.
      </Typography>

      <FormControlLabel
        control={
          <Checkbox checked={usePageBreaks} onChange={(e) => setUsePageBreaks(e.target.checked)} />
        }
        label="Use page breaks and common chapter patterns (Chapter 1, Part I, etc.)"
      />

      <FormControlLabel
        control={
          <Checkbox checked={skipTitlePage} onChange={(e) => setSkipTitlePage(e.target.checked)} />
        }
        label="Skip title page (first page)"
      />

      <Divider>
        <Typography variant="body2" color="text.secondary">
          OR
        </Typography>
      </Divider>

      <Box>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Custom chapter pattern (regex or text):
        </Typography>
        <TextField
          fullWidth
          placeholder="e.g., Chapter \d+, or --- for scene breaks"
          value={chapterPattern}
          onChange={(e) => {
            setChapterPattern(e.target.value);
            if (e.target.value) setUsePageBreaks(false);
          }}
          helperText="Leave empty to use default detection. Enter a pattern to split chapters."
          sx={{
            '& .MuiInputLabel-root': { color: colors?.text?.secondary || 'text.secondary' },
            '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
            input: { color: colors?.text?.primary || 'text.primary' },
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: colors?.border?.subtle || 'divider' },
              '&:hover fieldset': { borderColor: 'primary.main' },
              '&.Mui-focused fieldset': { borderColor: 'primary.main' },
            },
          }}
        />
      </Box>

      <Alert severity="info">
        If no chapter markers are detected, the entire document will be imported as a single
        chapter.
      </Alert>

      <Divider />

      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
          Extract from manuscript
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          PlotWyze can analyze your manuscript and pre-fill parts of your project.
        </Typography>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={extractCharacters}
                onChange={(e) => setExtractCharacters(e.target.checked)}
              />
            }
            label="Character sheets (names, roles, traits)"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={extractTimeline}
                onChange={(e) => setExtractTimeline(e.target.checked)}
              />
            }
            label="Timeline events (dates and story beats)"
          />
        </FormGroup>
      </Box>
    </Box>
  );

  const renderReviewStep = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {preview ? (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 2,
              p: 2,
              backgroundColor: colors?.paper?.elevated || 'rgba(148,163,184,0.08)',
              borderRadius: 1,
            }}
          >
            {[
              { label: 'File Name', value: preview.fileName },
              { label: 'File Size', value: `${(preview.fileSize / 1024).toFixed(1)} KB` },
              { label: 'Chapters Detected', value: preview.chaptersDetected },
              {
                label: 'Total Words',
                value: preview.totalWordCount?.toLocaleString() || 0,
              },
            ].map(({ label, value }) => (
              <Box key={label}>
                <Typography variant="body2" color="text.secondary">
                  {label}
                </Typography>
                <Typography variant="body1">{value}</Typography>
              </Box>
            ))}
          </Box>

          {preview.breakdown && (
            <>
              <Divider />
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                Manuscript Breakdown:
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                  gap: 2,
                  p: 2,
                  backgroundColor: colors?.paper?.elevated || 'rgba(148,163,184,0.08)',
                  borderRadius: 1,
                }}
              >
                {[
                  { label: 'Chapters', value: preview.breakdown.chapters || 0 },
                  { label: 'Scenes', value: preview.breakdown.scenes || 0 },
                  {
                    label: 'Total Words',
                    value: preview.breakdown.totalWords?.toLocaleString() || 0,
                  },
                  ...(preview.breakdown.characters > 0
                    ? [{ label: 'Characters', value: preview.breakdown.characters }]
                    : []),
                  ...(preview.breakdown.timelineEvents > 0
                    ? [{ label: 'Timeline Events', value: preview.breakdown.timelineEvents }]
                    : []),
                ].map(({ label, value }) => (
                  <Box key={label} sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" color="primary">
                      {value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}

          <Divider />

          <Typography variant="subtitle2">Chapters to be created:</Typography>
          <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
            {preview.chapters?.map((chapter, chapterIndex) => (
              <Box
                key={chapter.name || chapter.id || chapterIndex}
                sx={{
                  mb: 1,
                  border: `1px solid ${colors?.border?.subtle || '#eee'}`,
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1.5,
                    backgroundColor: colors?.paper?.elevated || 'rgba(148,163,184,0.08)',
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {chapter.index || chapterIndex + 1}. {chapter.name}
                  </Typography>
                  <Chip
                    label={`${chapter.wordCount?.toLocaleString() || 0} words`}
                    size="small"
                    variant="outlined"
                    color="primary"
                  />
                </Box>
                {chapter.scenes && chapter.scenes.length > 0 && (
                  <Box sx={{ pl: 2, pr: 1, pb: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 0.5 }}
                    >
                      Scenes ({chapter.scenes.length}):
                    </Typography>
                    {chapter.scenes.map((scene, sceneIndex) => (
                      <Box
                        key={scene.name || sceneIndex}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          py: 0.5,
                          pl: 2,
                          borderLeft: `2px solid ${colors?.border?.subtle || '#ddd'}`,
                        }}
                      >
                        <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                          • {scene.name || `Scene ${scene.index || sceneIndex + 1}`}
                        </Typography>
                        {scene.wordCount > 0 && (
                          <Chip
                            label={`${scene.wordCount.toLocaleString()} words`}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            ))}
          </Box>

          {preview.warnings?.length > 0 && (
            <Alert severity="warning">
              {preview.warnings.length} warning(s) during parsing. Some content may not import
              perfectly.
            </Alert>
          )}

          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            Formatting to preserve:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {Object.entries(formattingOptions)
              .filter(([, value]) => value)
              .map(([key]) => (
                <Chip key={key} label={key.charAt(0).toUpperCase() + key.slice(1)} size="small" />
              ))}
          </Box>
        </>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );

  const renderActionButton = () => {
    if (isScratchLastStep) {
      return (
        <Button
          onClick={handleCreateScratch}
          variant="contained"
          color="primary"
          disabled={!canProceed() || loading}
          sx={{ minWidth: 96 }}
        >
          {loading ? <CircularProgress size={24} /> : 'Create'}
        </Button>
      );
    }
    if (isImportLastStep) {
      return (
        <Button
          onClick={handleImport}
          variant="contained"
          color="primary"
          disabled={!canProceed() || loading}
          sx={{ minWidth: 96 }}
        >
          {loading ? <CircularProgress size={24} /> : 'Import'}
        </Button>
      );
    }
    return (
      <Button
        onClick={handleNext}
        variant="contained"
        color="primary"
        disabled={!canProceed() || loading}
        sx={{ minWidth: 96 }}
      >
        {loading ? <CircularProgress size={24} /> : 'Next'}
      </Button>
    );
  };

  const renderStepContent = () => {
    const label = steps[activeStep];
    switch (label) {
      case 'Get Started':
        return renderGetStartedStep();
      case 'Project Details':
        return renderProjectDetailsStep();
      case 'Upload File':
        return renderFileUploadStep();
      case 'Project':
        return renderProjectSelectionStep();
      case 'Formatting':
        return renderFormattingStep();
      case 'Chapter Detection':
        return renderChapterDetectionStep();
      case 'Review':
        return renderReviewStep();
      default:
        return null;
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <ReusableModal
      open={open}
      onClose={handleClose}
      title="Create a Project"
      width={600}
      showCloseButton
      ariaLabelledby="create-project-modal-title"
      ariaDescribedby="create-project-modal-description"
      contentSx={{ gap: 0, display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      {/* Fixed Header: Stepper + Error */}
      <Box sx={{ flexShrink: 0 }}>
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{
            mb: 3,
            '& .MuiStepLabel-label': {
              color: colors?.text?.secondary || 'text.secondary',
              '&.Mui-active,&.Mui-completed': {
                color: colors?.text?.primary || 'text.primary',
              },
            },
            '& .MuiStepIcon-root': {
              color: colors?.border?.subtle || 'rgba(148,163,184,0.8)',
              '&.Mui-active,&.Mui-completed': {
                color: colors?.primary?.main || 'primary.main',
              },
            },
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
      </Box>

      {/* Scrollable Content */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          minHeight: 280,
          pr: 1,
          '&::-webkit-scrollbar': { width: '8px' },
          '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: colors?.border?.subtle || 'rgba(0,0,0,0.2)',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: colors?.border?.primary || 'rgba(0,0,0,0.3)',
            },
          },
        }}
      >
        {renderStepContent()}
      </Box>

      {/* Fixed Footer */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mt: 3,
          pt: 2,
          borderTop: `1px solid ${colors?.border?.subtle || 'divider'}`,
          flexShrink: 0,
        }}
      >
        <Button
          onClick={handleBack}
          disabled={activeStep === 0 || loading}
          variant="outlined"
          sx={{
            color: colors?.text?.secondary || 'text.secondary',
            borderColor: colors?.border?.subtle || 'divider',
            '&:hover': {
              color: colors?.text?.primary || 'text.primary',
              borderColor: colors?.border?.primary || 'primary.main',
            },
          }}
        >
          Back
        </Button>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            disabled={loading}
            sx={{
              color: colors?.text?.secondary || 'text.secondary',
              borderColor: colors?.border?.subtle || 'divider',
              '&:hover': {
                color: colors?.text?.primary || 'text.primary',
                borderColor: colors?.border?.primary || 'primary.main',
              },
            }}
          >
            Cancel
          </Button>

          {renderActionButton()}
        </Box>
      </Box>
    </ReusableModal>
  );
};

CreateProjectModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onProjectCreated: PropTypes.func.isRequired,
  onImportComplete: PropTypes.func,
  projects: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      title: PropTypes.string,
      type: PropTypes.string,
      thinChapters: PropTypes.arrayOf(PropTypes.shape({})),
    })
  ),
};

CreateProjectModal.defaultProps = {
  onImportComplete: null,
  projects: [],
};

export default CreateProjectModal;
